import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import constants from "../../../../utils/constants";

const GOLD = "#cea05f";
const GOLD_DARK = "#a97142";
const NAVY = "#1a2b4a";
const NAVY_MID = "#243b66";

const FEATURES = [
  {
    icon: <AddCircleOutlineIcon sx={{ fontSize: 26 }} />,
    title: "Create Products",
    description:
      "Push new inventory from WatchOps to your Shopify store in a few clicks.",
  },
  {
    icon: <EditOutlinedIcon sx={{ fontSize: 26 }} />,
    title: "Edit & Update",
    description:
      "Keep product details, pricing, and availability in sync when you edit in WatchOps.",
  },
  {
    icon: <SyncAltIcon sx={{ fontSize: 26 }} />,
    title: "Inventory Sync",
    description:
      "Sync selected inventories to Shopify so your storefront stays up to date.",
  },
  {
    icon: <Inventory2OutlinedIcon sx={{ fontSize: 26 }} />,
    title: "Stock Visibility",
    description:
      "Include watches in Shopify from inventory so customers see accurate availability.",
  },
];

const HIGHLIGHTS = [
  "Create and manage Shopify-linked inventory",
  "Edit product details and keep pricing in sync",
  "Sync selected inventories to your storefront",
  "Control stock visibility for customers",
];

const ShopifyConfigPage = () => {
  const handleProceedInstall = () => {
    const appUrl = constants.APP_URL;
    if (!appUrl) {
      console.error("APP_URL is not defined in constants");
      return;
    }
    // Same-tab only — do not use shopifyAppSetup popup name (profile flow)
    try {
      sessionStorage.setItem("shopifyInstallSource", "config");
    } catch (e) {}
    window.location.assign(appUrl);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f3f5f8",
        py: { xs: 3, md: 4 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1100,
          mx: "auto",
          border: `2px solid ${GOLD}`,
          borderRadius: 3,
          overflow: "hidden",
          background: "#ffffff",
          boxShadow: "0 10px 36px rgba(26,43,74,0.08)",
          "@keyframes fadeUp": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeUp 0.4s ease-out",
        }}
      >
        {/* Header banner */}
        <Box
          sx={{
            background: `linear-gradient(100deg, ${NAVY} 0%, ${NAVY_MID} 55%, #2f4a7a 100%)`,
            px: { xs: 3, md: 4 },
            py: { xs: 3, md: 3.75 },
            color: "#fff",
          }}
        >
          <Box sx={{ maxWidth: 720 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  border: "1.5px solid rgba(255,255,255,0.85)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 12, color: "#fff" }} />
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: "13px !important",
                  letterSpacing: "0.08em",
                  fontWeight: 500,
                  color: "rgba(255,255,255,0.9) !important",
                  textTransform: "uppercase",
                  lineHeight: 1.4,
                }}
              >
                WatchOps Integrations
              </Box>
            </Stack>
            <Box
              component="h1"
              sx={{
                margin: "0 0 12px 0",
                fontSize: { xs: "28px !important", md: "36px !important" },
                fontWeight: "700 !important",
                color: "#fff !important",
                lineHeight: "1.2 !important",
                fontFamily: "inherit",
              }}
            >
              Welcome to WatchOps
            </Box>
            <Box
              component="p"
              sx={{
                margin: 0,
                fontSize: "14px !important",
                fontWeight: "400 !important",
                color: "rgba(255,255,255,0.85) !important",
                lineHeight: "1.55 !important",
                maxWidth: 640,
              }}
            >
              Connect WatchOps with your Shopify store to create, edit, and sync
              inventory — all from one place. Install the app to unlock full
              marketplace capabilities.
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: { xs: 2.5, md: 4 }, background: "#f8f9fb" }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 2.5 }}
          >
            <CheckCircleIcon sx={{ color: "#22C55E", fontSize: 22 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: "#111827",
                fontSize: "1.15rem !important",
              }}
            >
              Shopify Configuration
            </Typography>
          </Stack>

          {/* Main Shopify card */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2.5,
              border: "1px solid #E5E7EB",
              background: "#ffffff",
              boxShadow: "0 4px 18px rgba(0,0,0,0.04)",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                mb={2}
              >
                <Stack direction="row" spacing={1.75} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: "rgba(34,197,94,0.12)",
                      color: "#16A34A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <StorefrontIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#111827",
                        fontSize: "1.15rem !important",
                      }}
                    >
                      Shopify
                    </Typography>
                    <Typography
                      sx={{
                        color: "#6B7280 !important",
                        fontSize: "13px !important",
                      }}
                    >
                      Third-Party Integration
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  label="Live"
                  size="small"
                  sx={{
                    bgcolor: "rgba(34,197,94,0.12)",
                    color: "#15803D",
                    fontWeight: 700,
                    border: "1px solid rgba(34,197,94,0.35)",
                  }}
                />
              </Stack>

              <Typography
                sx={{
                  color: "#4B5563 !important",
                  fontSize: "14px !important",
                  lineHeight: 1.6,
                  mb: 2.5,
                }}
              >
                Install the WatchOps Shopify app to connect your store, then
                configure shop details from Profile to start syncing inventory.
              </Typography>

              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "13px !important",
                  mb: 1.25,
                  letterSpacing: "0.02em",
                }}
              >
                Highlights
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.25,
                  mb: 3,
                  "& li": {
                    color: "#4B5563",
                    fontSize: "13.5px",
                    lineHeight: 1.7,
                    mb: 0.35,
                  },
                }}
              >
                {HIGHLIGHTS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </Box>

              <Button
                variant="contained"
                startIcon={<SettingsOutlinedIcon />}
                endIcon={<ArrowForwardIcon />}
                onClick={handleProceedInstall}
                sx={{
                  bgcolor: GOLD,
                  color: "#fff",
                  cursor: "pointer",
                  px: 2.75,
                  py: 1.15,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "14px",
                  boxShadow: "0 4px 14px rgba(206,160,95,0.35)",
                  transition:
                    "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
                  "& .MuiButton-endIcon": {
                    transition: "transform 0.2s ease",
                  },
                  "&:hover": {
                    bgcolor: GOLD_DARK,
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 20px rgba(169,113,66,0.4)",
                    "& .MuiButton-endIcon": {
                      transform: "translateX(4px)",
                    },
                  },
                  "&:active": {
                    transform: "translateY(0) scale(0.98)",
                  },
                }}
              >
                Proceed to App Install
              </Button>
            </CardContent>
          </Card>

          {/* Capability cards */}
          <Typography
            sx={{
              fontWeight: 700,
              color: "#111827",
              fontSize: "1.05rem !important",
              mb: 2,
            }}
          >
            What you can do
          </Typography>

          <Grid container spacing={2.5}>
            {FEATURES.map((feature, index) => (
              <Grid item xs={12} sm={6} key={feature.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 2.5,
                    border: "1px solid #E5E7EB",
                    background: "#ffffff",
                    cursor: "default",
                    animation: "fadeUp 0.4s ease-out",
                    animationDelay: `${0.07 * (index + 1)}s`,
                    animationFillMode: "both",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        mb: 1.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(206,160,95,0.14)",
                        color: GOLD_DARK,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#111827",
                        fontSize: "0.98rem !important",
                        mb: 0.6,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#4B5563 !important",
                        fontSize: "13px !important",
                        lineHeight: 1.55,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Footer tip */}
          <Card
            elevation={0}
            sx={{
              mt: 3,
              borderRadius: 2.5,
              border: "1px solid #E5E7EB",
              background: "#ffffff",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "1rem !important",
                }}
              >
                Ready to get started?
              </Typography>
              <Typography
                sx={{
                  color: "#6B7280 !important",
                  fontSize: "13.5px !important",
                  mt: 0.5,
                }}
              >
                After install, configure your exact Shopify store name from
                Profile so verification completes successfully.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ShopifyConfigPage;
